import { useCallback, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { NexusAgent } from '../../../../domain/agents/models/Agent';
import { ChatProviderType } from '../../../../domain/chat/models/ChatProviderType';
import { acquireNexusApiAccessTokenAsync } from '../../../../services/auth/AuthService';
import { createChatSessionHandler } from '../../../../application/chat-sessions/handlers/CreateChatSessionHandler';
import { useProviderServices } from '../../../../app/providers/AppServicesContext';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setDocuments } from '../../../../redux/features/chatDocuments/chatDocumentsSlice';
import { upsertSession } from '../../../../redux/features/chatSessions/chatSessionsSlice';
import { setSelectedSessionId } from '../../../../redux/features/chatUi/chatUiSlice';
import { selectActiveChatSessionCount } from '../../../../domain/selectors/base/chat/selectActiveChatSessionCount';
import { alerts } from '../../../../shared/ui/Alerts';
import { isNexusAgent } from '../../../utils/AgentUtils';

const createChatSessionToastMessages = {
    pending: 'Creating chat...',
    success: 'Chat created successfully!',
    error: 'Failed to create a new chat. Please try again.',
};

export interface ExploreAgentsPageVm {
    agents: NexusAgent[];
    chatCount: number;
    allowedChatCount: number | null;
    isMaxChatsReached: boolean;
    createAgentChat: (agent: NexusAgent) => Promise<void>;
}

export const useExploreAgentsPage = (): ExploreAgentsPageVm => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const providerServices = useProviderServices();
    const { instance } = useMsal();
    const agentsById = useAppSelector((state) => state.agents.byId);
    const agentIds = useAppSelector((state) => state.agents.allIds);
    const chatCount = useAppSelector(selectActiveChatSessionCount);

    const allowedChatCount = useMemo(() => {
        const parsedValue = Number(process.env.REACT_APP_CHAT_SESSION_COUNT ?? '');

        if (!Number.isFinite(parsedValue) || parsedValue <= 0) {
            return null;
        }

        return parsedValue;
    }, []);

    const agents = useMemo(() => {
        return agentIds
            .map((agentId) => agentsById[agentId])
            .filter(isNexusAgent)
            .sort((left, right) => left.name.localeCompare(right.name));
    }, [agentIds, agentsById]);

    // repetitive logic. can be extracted
    const isMaxChatsReached = allowedChatCount !== null && chatCount >= allowedChatCount;

    // repetitive logic. can be extracted
    const createAgentChat = useCallback(
        async (agent: NexusAgent): Promise<void> => {
            if (allowedChatCount !== null && chatCount >= allowedChatCount) {
                alerts.error(
                    `You have reached the maximum limit of ${allowedChatCount} active chat sessions. Please delete an existing chat to create a new one.`,
                );
                return;
            }

            const accessToken = await acquireNexusApiAccessTokenAsync({
                msalInstance: instance,
            });

            try {
                const createdSession = await alerts.promise(
                    createChatSessionHandler({
                        request: {
                            providerType: ChatProviderType.Nexus,
                            agentId: agent.agentId,
                            title: `${agent.name} Chat`,
                            enableFileSearch: agent.allowFileSearchAtConversationLevel ?? false,
                            enableCodeInterpreter: agent.allowCodeInterpreterAtConversationLevel ?? false,
                        },
                        accessToken,
                        providerServices,
                    }),
                    createChatSessionToastMessages,
                );

                dispatch(upsertSession(createdSession.session));
                dispatch(
                    setDocuments({
                        sessionId: createdSession.session.id,
                        documents: createdSession.documents,
                    }),
                );
                dispatch(setSelectedSessionId(createdSession.session.id));
                navigate(`/chat/${createdSession.session.id}`);
            } catch {
                return;
            }
        },
        [allowedChatCount, chatCount, dispatch, instance, navigate, providerServices],
    );

    return {
        agents,
        chatCount,
        allowedChatCount,
        isMaxChatsReached,
        createAgentChat,
    };
};

export default useExploreAgentsPage;
